/**
 * Dashboard section wrapper: header, edit chrome, and the middle slot for {@link KpiGrid}.
 */

import * as React from 'react';
import { BarChart2, Check, Filter, Pencil, X } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { KpiGrid, type KpiGridHandle } from '@/components/kpi/KpiGrid';
import { KPI_LABELS } from '@/components/kpi/kpi-configs';
import { cn } from '@/lib/utils';

/** Props for {@link KpiSection}. */
interface KpiSectionProps {
  editMode?: boolean;
  onToggleEditMode?: () => void;
}

/** Dashboard widget: KPI cards. */
export const KpiSection: React.FC<KpiSectionProps> = ({
  editMode: externalEditMode,
  onToggleEditMode,
}) => {
  const [internalEditMode, setInternalEditMode] = React.useState(false);
  const editMode = externalEditMode ?? internalEditMode;
  const [hiddenCount, setHiddenCount] = React.useState(0);
  const [isSaving, setIsSaving] = React.useState(false);
  const [showFilters, setShowFilters] = React.useState(false);
  const [hasFilters, setHasFilters] = React.useState(false);
  const kpiRef = React.useRef<KpiGridHandle>(null);

  React.useEffect(() => {
    if (editMode) setShowFilters(false);
  }, [editMode]);

  /** Open or close the date-filter toolbar. */
  function handleToggleFilters(): void {
    setShowFilters((prev) => !prev);
  }

  /** Enter or leave edit mode (parent callback or local state). */
  function handleToggleEdit(): void {
    if (onToggleEditMode) {
      onToggleEditMode();
      return;
    }
    setInternalEditMode(!internalEditMode);
  }

  /** Persist via the KPI body, then leave edit mode. */
  function handleSaveAndClose(): void {
    kpiRef.current?.saveAndClose();
  }

  /** Restore the snapshot, then leave edit mode. */
  function handleCancel(): void {
    kpiRef.current?.cancel();
  }

  /** Leave edit mode after a successful save or an explicit cancel. */
  function handleExitEdit(): void {
    if (onToggleEditMode) {
      onToggleEditMode();
      return;
    }
    setInternalEditMode(false);
  }

  /** Hidden-card count in the title while editing. */
  function renderHiddenCount(): React.ReactNode {
    if (!editMode || hiddenCount === 0) return null;
    return (
      <span className="ml-2 text-sm font-normal text-muted-foreground">
        {KPI_LABELS.hiddenCount(hiddenCount)}
      </span>
    );
  }

  /** Filter chip — same header row as edit, hidden when no IE filter exists. */
  function renderFilterButton(): React.ReactNode {
    if (!hasFilters) return null;
    return (
      <Button
        preset="section-action"
        icon={Filter}
        tooltipText={KPI_LABELS.filter}
        className={cn(showFilters && 'section-action-button-active')}
        onClick={handleToggleFilters}
        disabled={editMode}
      />
    );
  }

  /** Cancel chip — only in edit mode. */
  function renderCancelButton(): React.ReactNode {
    if (!editMode) return null;
    return (
      <Button
        preset="section-action"
        icon={X}
        tooltipText={KPI_LABELS.cancel}
        onClick={handleCancel}
      />
    );
  }

  return (
    <div className="space-y-5">
      <div className="section-header">
        <div className="flex items-center gap-2 text-[var(--color-1-nuance-1)]">
          <BarChart2 className="h-5 w-5" />
          <h2 className="section-title">
            Statistiques
            {renderHiddenCount()}
          </h2>
        </div>
        <div className="section-actions">
          {renderFilterButton()}
          {renderCancelButton()}
          <Button
            preset="section-action"
            icon={editMode ? Check : Pencil}
            tooltipText={editMode ? KPI_LABELS.finish : KPI_LABELS.edit}
            className={cn(editMode && 'section-action-button-active')}
            onClick={editMode ? handleSaveAndClose : handleToggleEdit}
            disabled={editMode && isSaving}
          />
        </div>
      </div>
      <div>
        <KpiGrid
          ref={kpiRef}
          editMode={editMode}
          onHiddenCountChange={setHiddenCount}
          onSavingChange={setIsSaving}
          onExitEdit={handleExitEdit}
          showFilters={showFilters}
          onShowFiltersChange={setShowFilters}
          onHasFiltersChange={setHasFilters}
        />
      </div>
    </div>
  );
};
